/**
 * Project card component
 */

import { Card, CardContent, CardActions, Typography, Chip, Stack, Box, Button, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GitHubIcon from '@mui/icons-material/GitHub';
import ArticleIcon from '@mui/icons-material/Article';
import CodeIcon from '@mui/icons-material/Code';
import { Project } from '@/types/project';
import { pageColors } from '@/theme/colors';
import { analytics } from '@/lib/analytics';

interface ProjectCardProps {
  project: Project;
  onLearnMore?: (project: Project) => void;
}

const statusConfig = {
  active: { label: 'Active', color: '#10b981' },
  completed: { label: 'Completed', color: '#3b82f6' },
  archived: { label: 'Archived', color: '#6b7280' },
  'coming-soon': { label: 'Coming Soon', color: '#f59e0b' },
} as const;

const categoryConfig = {
  'open-source': { label: 'Open Source', color: '#06b6d4' },
  personal: { label: 'Personal', color: '#a855f7' },
  experiment: { label: 'Experiment', color: '#ec4899' },
  professional: { label: 'Professional', color: '#3b82f6' },
} as const;

export function ProjectCard({ project, onLearnMore }: ProjectCardProps) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const cyan = dark ? pageColors.projects.accent : pageColors.projects.primary;
  const cyanBg = dark ? 'rgba(103, 232, 249, 0.12)' : 'rgba(6, 182, 212, 0.1)';
  const cyanBgHover = dark ? 'rgba(103, 232, 249, 0.2)' : 'rgba(6, 182, 212, 0.2)';

  const status = statusConfig[project.status];
  const category = categoryConfig[project.category];

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        ...(project.featured && {
          borderTop: `3px solid ${cyan}`,
        }),
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: dark
            ? '0 12px 24px rgba(103, 232, 249, 0.12)'
            : '0 12px 24px rgba(6, 182, 212, 0.15)',
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Stack spacing={2}>
          {/* Chips row: status + category + featured */}
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              label={status.label}
              size="small"
              sx={{
                bgcolor: `${status.color}18`,
                color: status.color,
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 24,
                border: `1px solid ${status.color}40`,
              }}
            />
            <Chip
              label={category.label}
              size="small"
              variant="outlined"
              sx={{
                borderColor: `${category.color}60`,
                color: category.color,
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 24,
              }}
            />
            {project.featured && (
              <Chip
                label="Featured"
                size="small"
                sx={{
                  bgcolor: dark ? 'rgba(103, 232, 249, 0.15)' : 'rgba(6, 182, 212, 0.1)',
                  color: cyan,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: 24,
                  border: `1px solid ${dark ? 'rgba(103, 232, 249, 0.3)' : 'rgba(6, 182, 212, 0.25)'}`,
                }}
              />
            )}
          </Stack>

          {/* Title */}
          <Typography
            variant="h6"
            component="h2"
            fontWeight="bold"
            sx={{ lineHeight: 1.3 }}
          >
            {project.title}
          </Typography>

          {/* Description */}
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {project.description}
          </Typography>

          {/* Used By Section */}
          {project.usedBy && project.usedBy.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, display: 'block' }}>
                Used By
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {project.usedBy.map((user, idx) => (
                  <Chip
                    key={idx}
                    label={user.name}
                    size="small"
                    component="a"
                    href={user.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    clickable
                    sx={{
                      bgcolor: cyanBg,
                      '&:hover': { bgcolor: cyanBgHover },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Tags */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {project.tags.slice(0, 6).map(tag => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                sx={{
                  bgcolor: cyanBg,
                  color: cyan,
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  '&:hover': { bgcolor: cyanBgHover },
                }}
              />
            ))}
            {project.tags.length > 6 && (
              <Chip
                label={`+${project.tags.length - 6} more`}
                size="small"
                sx={{
                  bgcolor: dark ? 'rgba(103, 232, 249, 0.06)' : 'rgba(6, 182, 212, 0.05)',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                }}
              />
            )}
          </Stack>
        </Stack>
      </CardContent>

      {/* Actions */}
      <CardActions sx={{ p: 3, pt: 0, gap: 1, flexWrap: 'wrap' }}>
        {onLearnMore && (
          <Button
            size="small"
            variant="contained"
            onClick={() => onLearnMore(project)}
            endIcon={<ArticleIcon />}
            sx={{
              background: pageColors.projects.gradient,
              color: dark ? '#0a0a0a' : 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
              },
            }}
          >
            Learn More
          </Button>
        )}

        {project.githubUrl && (
          <Tooltip title="View on GitHub">
            <IconButton
              size="small"
              component="a"
              href={project.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => analytics.projectLinkClicked(project.id, 'github')}
              sx={{ color: 'text.secondary', '&:hover': { color: cyan } }}
            >
              <GitHubIcon />
            </IconButton>
          </Tooltip>
        )}

        {project.npmUrl && (
          <Tooltip title="View on NPM">
            <IconButton
              size="small"
              component="a"
              href={project.npmUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => analytics.projectLinkClicked(project.id, 'npm')}
              sx={{ color: 'text.secondary', '&:hover': { color: cyan } }}
            >
              <CodeIcon />
            </IconButton>
          </Tooltip>
        )}

        {project.luaRocksUrl && (
          <Tooltip title="View on LuaRocks">
            <IconButton
              size="small"
              component="a"
              href={project.luaRocksUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => analytics.projectLinkClicked(project.id, 'luarocks')}
              sx={{ color: 'text.secondary', '&:hover': { color: cyan } }}
            >
              <CodeIcon />
            </IconButton>
          </Tooltip>
        )}

        {project.demoUrl && (
          <Tooltip title="View Live Demo">
            <IconButton
              size="small"
              component="a"
              href={project.demoUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => analytics.projectLinkClicked(project.id, 'demo')}
              sx={{ color: 'text.secondary', '&:hover': { color: cyan } }}
            >
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
        )}

        {project.blogPostUrl && (
          <Tooltip title="Read Blog Post">
            <IconButton
              size="small"
              component="a"
              href={project.blogPostUrl}
              onClick={() => analytics.projectLinkClicked(project.id, 'blog')}
              sx={{ color: 'text.secondary', '&:hover': { color: cyan } }}
            >
              <ArticleIcon />
            </IconButton>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  );
}
